// Client-side ability state manager

import type { AbilityDef, AbilityStateSnapshot, BuffStateWire } from '../../shared/protocol.ts';
import type { Coord } from '../../shared/abilityLogic.ts';

/** An ability definition merged with its live per-game state. */
export type ClientAbilityState = AbilityDef & {
    usesRemaining: number | null;
    cooldownRemaining: number;
    maxUses: number | null;
    cooldownTurns: number;
};

/** Shape returned by getState() (local mirror of the server snapshot). */
export interface AbilityStateBundle {
    abilities: Record<string, ClientAbilityState>;
    buffs: Record<string, BuffStateWire>;
    extraShots: number;
}

/** A decoy placement on the player's own board. */
export interface DecoyPosition {
    id?: string;
    x: number;
    y: number;
    cells?: Coord[];
    revealed: boolean;
}

type AbilityEventHandler = (data?: unknown) => void;

export class AbilityManager {
    declare abilities: Record<string, ClientAbilityState>;
    declare buffs: Record<string, BuffStateWire>;
    declare extraShots: number;
    declare targetingAbility: string | null;
    declare listeners: Map<string, AbilityEventHandler[]>;
    declare scannedShips: string[];
    declare decoyPositions: DecoyPosition[];
    declare opponentAbilityState: AbilityStateSnapshot | null;
    declare decoyHorizontal: boolean;

    constructor() {
        this.abilities = {};
        this.buffs = {};
        this.extraShots = 0;
        this.targetingAbility = null; // Currently selected ability for targeting
        this.listeners = new Map();
        this.scannedShips = [];  // Track which ships have been scanned
        this.decoyPositions = []; // Track decoy placements on own board
        this.opponentAbilityState = null; // Track opponent's ability state
        this.decoyHorizontal = true; // Orientation for placing multi-cell decoys
    }

    // Initialize abilities from faction data
    initialize(factionAbilityData: AbilityDef[] | null | undefined): void {
        this.abilities = {};
        this.buffs = {};
        this.extraShots = 0;
        this.targetingAbility = null;
        this.scannedShips = [];
        this.decoyPositions = [];
        this.opponentAbilityState = null;
        this.decoyHorizontal = true;
        this.listeners.clear();

        if (!factionAbilityData || !Array.isArray(factionAbilityData)) {
            return;
        }

        for (const ability of factionAbilityData) {
            this.abilities[ability.id] = {
                ...ability,
                usesRemaining: ability.limits?.usesPerGame ?? null,
                cooldownRemaining: 0,
                maxUses: ability.limits?.usesPerGame ?? null,
                cooldownTurns: ability.limits?.cooldownTurns ?? 0
            };
        }

        this.emit('initialized', this.abilities);
    }

    // Update state from server
    updateFromServer(serverState: AbilityStateSnapshot | null | undefined): void {
        if (!serverState) return;

        if (serverState.abilities) {
            for (const [id, state] of Object.entries(serverState.abilities)) {
                if (this.abilities[id]) {
                    this.abilities[id].usesRemaining = state.usesRemaining;
                    this.abilities[id].cooldownRemaining = state.cooldownRemaining;
                }
            }
        }

        this.buffs = serverState.buffs || {};
        this.extraShots = serverState.extraShots || 0;

        this.emit('updated', this.getState());
    }

    // Update opponent's ability state
    setOpponentState(state: AbilityStateSnapshot | null): void {
        this.opponentAbilityState = state;
        this.emit('opponentStateUpdated', state);
    }

    // Get opponent's ability state
    getOpponentState(): AbilityStateSnapshot | null {
        return this.opponentAbilityState;
    }

    // Get current ability state
    getState(): AbilityStateBundle {
        return {
            abilities: this.abilities,
            buffs: this.buffs,
            extraShots: this.extraShots
        };
    }

    // Get active abilities
    getActiveAbilities(): ClientAbilityState[] {
        return Object.values(this.abilities).filter(a => a.type === 'active');
    }

    // Get passive abilities
    getPassiveAbilities(): ClientAbilityState[] {
        return Object.values(this.abilities).filter(a => a.type === 'passive');
    }

    // Check if an ability can be used
    canUseAbility(abilityId: string): boolean {
        const ability = this.abilities[abilityId];
        if (!ability || ability.type !== 'active') return false;

        // Check uses remaining
        if (ability.usesRemaining !== null && ability.usesRemaining <= 0) {
            return false;
        }

        // Check cooldown
        if (ability.cooldownRemaining > 0) {
            return false;
        }

        return true;
    }

    // Get reason why ability can't be used
    getAbilityBlockedReason(abilityId: string): string | null {
        const ability = this.abilities[abilityId];
        if (!ability) return 'Ability not found';
        if (ability.type !== 'active') return 'Not an active ability';

        if (ability.usesRemaining !== null && ability.usesRemaining <= 0) {
            return 'No uses remaining';
        }

        if (ability.cooldownRemaining > 0) {
            return `On cooldown (${ability.cooldownRemaining} turns)`;
        }

        return null;
    }

    // Select an ability for targeting
    selectAbility(abilityId: string): boolean {
        if (!this.canUseAbility(abilityId)) {
            return false;
        }

        this.targetingAbility = abilityId;
        this.emit('targeting', { abilityId, ability: this.abilities[abilityId] });
        return true;
    }

    // Cancel ability targeting
    cancelTargeting(): void {
        const was = this.targetingAbility;
        this.targetingAbility = null;
        if (was) {
            this.emit('targeting-cancelled', { abilityId: was });
        }
    }

    // Check if currently targeting with an ability
    isTargeting(): boolean {
        return this.targetingAbility !== null;
    }

    // Get current targeting ability
    getTargetingAbility(): ClientAbilityState | null {
        return this.targetingAbility ? this.abilities[this.targetingAbility] : null;
    }

    // Check if has extra shots
    hasExtraShots(): boolean {
        return this.extraShots > 0;
    }

    // Add a decoy position (supports single cell or multi-cell)
    // cells can be array of {x, y} or omitted for single cell at (x, y)
    addDecoy(x: number, y: number, cells: Coord[] | null = null): void {
        if (cells && cells.length > 1) {
            // Multi-cell decoy
            const id = `decoy-${Date.now()}`;
            this.decoyPositions.push({
                id,
                x, y,
                cells,
                revealed: false
            });
        } else {
            // Single cell decoy (backwards compatible)
            this.decoyPositions.push({ x, y, revealed: false });
        }
    }

    // Mark a decoy as revealed - returns all cells that were revealed
    revealDecoy(x: number, y: number): Coord[] {
        // Find decoy by checking if (x,y) matches origin or any cell
        const decoy = this.decoyPositions.find(d => {
            if (d.revealed) return false;
            if (d.x === x && d.y === y) return true;
            if (d.cells) {
                return d.cells.some(c => c.x === x && c.y === y);
            }
            return false;
        });

        if (decoy) {
            decoy.revealed = true;
            return decoy.cells || [{ x: decoy.x, y: decoy.y }];
        }
        return [{ x, y }];
    }

    // Toggle decoy placement orientation
    toggleDecoyOrientation(): boolean {
        this.decoyHorizontal = !this.decoyHorizontal;
        return this.decoyHorizontal;
    }

    // Get current decoy orientation
    getDecoyHorizontal(): boolean {
        return this.decoyHorizontal;
    }

    // Event listener methods
    on(event: string, callback: AbilityEventHandler): void {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event)!.push(callback);
    }

    off(event: string, callback: AbilityEventHandler): void {
        if (!this.listeners.has(event)) return;
        const callbacks = this.listeners.get(event)!;
        const index = callbacks.indexOf(callback);
        if (index !== -1) {
            callbacks.splice(index, 1);
        }
    }

    emit(event: string, data?: unknown): void {
        if (!this.listeners.has(event)) return;
        for (const callback of this.listeners.get(event)!) {
            callback(data);
        }
    }

    // Reset for new game
    reset(): void {
        this.abilities = {};
        this.buffs = {};
        this.extraShots = 0;
        this.targetingAbility = null;
        this.scannedShips = [];
        this.decoyPositions = [];
        this.emit('reset');
    }
}

// Singleton instance
export const abilityManager = new AbilityManager();
